import { useState, useEffect, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/browser';
import { getIdToken } from '@lib/auth';
import { generateClient } from 'aws-amplify/data';
import { useCompanyInfo } from '@contexts/company-info';
import type { AssistantRun, AssistantMessage } from '@src/types/assistant';
import type { Schema } from '@amplify/data/resource';
import amplifyConfig from '../../amplifyconfiguration.json';

const { awsRestApiUrl } = amplifyConfig.custom;
const POLL_INTERVAL = 2000;
const POLL_TIMEOUT = 240000; // 4 minutes

interface UseAssistantOptions {
  skip?: boolean;
}

function useAssistant(assistantMessage: AssistantMessage, options: UseAssistantOptions = {}) {
  const { skip = false } = options;

  const companyInfo = useCompanyInfo();
  const [data, setData] = useState<AssistantMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const startTime = useRef<Date | null>(null);
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasFetched = useRef(false);

  skip ? console.log('useAssistant() skipped!!!') : null;

  const isErrorResponse = async (response: Response) => {
    if (!response.ok) {
      try {
        const body = await response.json();
        setError(new Error(`API Error: status: ${response.status} message: ${body?.message || '<none>'}`));
      } catch (error) {
        Sentry.captureException(error, { extra: { error } });
        setError(new Error(`API Error: status: ${response.status} message: <none>`));
      }
      return true;
    }
    return false;
  };

  const fetchData = useCallback(async (runInfo: AssistantRun) => {
    console.log('fetchData()...');
    try {
      const url = `${awsRestApiUrl}result`;
      const idToken = await getIdToken();
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          threadId: runInfo.threadId,
          runId: runInfo.runId,
        }),
      });
      if (await isErrorResponse(response)) return null;

      const fetchResult = await response.json();
      console.log('useAssistant result: ', fetchResult);
      setData(fetchResult as AssistantMessage);
    } catch (error) {
      Sentry.captureException(error, { extra: { error } });
      setError(error as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  const startRun = useCallback(async () => {
    console.log('startRun()...');
    // prevent multiple calls, in development mode, this is called twice
    if (hasFetched.current) return;
    hasFetched.current = true;

    // Ensure to clear any existing polling before starting a new run
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }

    try {
      console.log('starting new run...');
      const url = `${awsRestApiUrl}run`;
      const idToken = await getIdToken();
      const runResponse = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(assistantMessage),
      });
      if (await isErrorResponse(runResponse)) return null;

      const runResult: AssistantRun = await runResponse.json();
      console.log('runResult:', runResult);
      return runResult;
    } catch (error) {
      Sentry.captureException(error, { extra: { error } });
      console.error('startRun error...', JSON.stringify(error, null, 2));
      //TODO: catch 504, gateway timeouts and try again.  Lambda timed out, so try again
      setError(error as Error);
      setLoading(false);
    }
  }, [assistantMessage]);

  const checkRunStatus = useCallback(
    async (runInfo: AssistantRun) => {
      console.log('checkRunStatus()...');

      //TODO: Test! don't loop endlessly
      if (startTime.current && new Date().getTime() - startTime.current.getTime() > POLL_TIMEOUT) {
        setError(new Error('Polling timeout exceeded'));
        setLoading(false);
        return;
      }

      try {
        console.log('checking run status...');
        const url = `${awsRestApiUrl}status`;
        const idToken = await getIdToken();
        const statusResponse = await fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            threadId: runInfo.threadId,
            runId: runInfo.runId,
          }),
        });
        // retry if we get an 504, gateway timeout
        if (statusResponse.status === 504) {
          console.log('Received an 504, gateway timeout, retrying...');
          pollTimeoutRef.current = setTimeout(() => checkRunStatus(runInfo), POLL_INTERVAL);
        }
        if (await isErrorResponse(statusResponse)) return null;

        const statusResult: AssistantRun = await statusResponse.json();
        console.log('new statusResult:', statusResult);

        if (statusResult.runStatus === 'completed') {
          pollTimeoutRef.current && clearTimeout(pollTimeoutRef.current);
          fetchData(statusResult);
          hasFetched.current = false;
        } else {
          // Continue polling
          pollTimeoutRef.current = setTimeout(() => checkRunStatus(runInfo), POLL_INTERVAL);
        }
      } catch (err) {
        // TODO: Fix!!! 504 Gateway Timeout, retrying...
        // console.error('checkRunStatus error msg: ', err.message, 'response: ', err.response);
        // if (err instanceof TypeError && err.status === 504) {
        //   console.log('Received a 504 Gateway Timeout, retrying...');
        //   pollTimeoutRef.current = setTimeout(() => checkRunStatus(runInfo), POLL_INTERVAL);
        // } else {
        Sentry.captureException(err, { extra: { err } });
        console.error('checkRunStatus error: ', err);
        //TODO: catch 504, gateway timeouts and try again.  Lambda timed out, so try again
        setError(err as Error);
        setLoading(false);
        // }
      }
    },
    [fetchData]
  );

  useEffect(() => {
    if (skip) return;
    setLoading(true);
    startRun().then((runInfo) => {
      if (runInfo) {
        const client = generateClient<Schema>();
        if (companyInfo.company?.id && runInfo.threadId) {
          client.models.Company.update({
            id: companyInfo.company?.id,
            threadId: runInfo.threadId,
          });
        } else {
          // TODO better error handling
          console.error(
            'Could not update company with threadId! companyInfo.company?.id || runInfo.threadId are null!'
          );
        }
        checkRunStatus(runInfo);
      }
    });

    return () => {
      // Cleanup polling if component unmounts
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, [startRun, checkRunStatus, skip]);

  return { data, loading, error };
}

export { useAssistant };
